import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Sparkles, 
  Users, 
  Clock, 
  MapPin, 
  CheckCircle, 
  XCircle,
  ChevronDown,
  ChevronUp,
  Quote,
  ArrowRight,
  Calendar,
  MessageCircle
} from 'lucide-react';

// Images for the gallery
const galleryImages = [
  {
    url: "https://images.pexels.com/photos/35553754/pexels-photo-35553754.jpeg",
    caption: "Cerimónia ao ar livre"
  },
  {
    url: "https://images.pexels.com/photos/35553749/pexels-photo-35553749.jpeg",
    caption: "Momentos únicos"
  },
  {
    url: "https://images.pexels.com/photos/35556978/pexels-photo-35556978.jpeg",
    caption: "Celebração de amor"
  },
  {
    url: "https://images.pexels.com/photos/35556977/pexels-photo-35556977.jpeg",
    caption: "Votos personalizados"
  }
];

// Example testimonials
const testimonials = [
  {
    couple: "Sofia & Miguel",
    text: "A cerimónia foi o momento mais emotivo do nosso casamento. A Véronique conseguiu captar exatamente quem somos e transformou a nossa história em palavras que nos fizeram chorar de felicidade. Os nossos convidados ainda falam dela!",
    ceremony: "Cerimónia no jardim da quinta",
    highlight: "Ritual da cápsula do tempo"
  },
  {
    couple: "Inês & Ricardo",
    text: "Não queríamos uma cerimónia tradicional e encontrámos na Véronique alguém que percebeu isso desde o primeiro momento. Cada palavra foi pensada para nós, incluindo os nossos inside jokes! Foi perfeito.",
    ceremony: "Cerimónia na praia ao pôr-do-sol",
    highlight: "Votos escritos pelos noivos"
  },
  {
    couple: "Catarina & André",
    text: "Tínhamos receio que uma cerimónia simbólica parecesse menos 'oficial', mas foi exatamente o oposto. Foi muito mais significativa do que qualquer cerimónia civil poderia ser. Sentimos que nos casámos de verdade ali.",
    ceremony: "Cerimónia em vinha centenária",
    highlight: "Ritual das mãos entrelaçadas"
  }
];

// FAQ data
const faqData = [
  {
    question: "A cerimónia simbólica tem valor legal?",
    answer: "Não, a cerimónia simbólica não tem valor jurídico. O casamento legal deve ser realizado na Conservatória ou através de cerimónia religiosa. Muitos casais optam por fazer o civil num momento mais íntimo e reservar a cerimónia simbólica para a celebração com todos os convidados."
  },
  {
    question: "Quanto tempo dura uma cerimónia simbólica?",
    answer: "Tipicamente entre 20 a 40 minutos, dependendo dos elementos incluídos. Uma cerimónia com rituais, leituras de familiares e votos personalizados será mais longa do que uma cerimónia mais simples. Juntos definimos a duração ideal para vocês."
  },
  {
    question: "Podemos incluir elementos religiosos?",
    answer: "Absolutamente! A cerimónia é completamente personalizável. Podemos incluir orações, bênçãos, leituras religiosas ou qualquer elemento espiritual que faça sentido para vocês, mesmo que não seja uma cerimónia religiosa formal."
  },
  {
    question: "Como funciona o processo de criação da cerimónia?",
    answer: "Começamos com uma conversa profunda sobre a vossa história, valores e visão para o dia. Depois, escrevo um primeiro rascunho da cerimónia que revisamos juntos. Fazemos ajustes até estar perfeita. Inclui também um ensaio antes do grande dia."
  },
  {
    question: "Podemos escrever os nossos próprios votos?",
    answer: "Claro! Encorajo muito os casais a escreverem os seus votos. Posso dar orientações e ajudar a estruturar se precisarem. Se preferirem, também posso escrever votos personalizados baseados nas vossas conversas comigo."
  },
  {
    question: "Que rituais simbólicos existem?",
    answer: "Há dezenas de opções: ritual da areia (união de duas cores), cerimónia das velas, ritual do vinho, cápsula do tempo, plantação de árvore, ritual das mãos entrelaçadas (handfasting), entre muitos outros. Também podemos criar rituais únicos que tenham significado especial para vocês."
  }
];

// Comparison data
const comparisonData = [
  { aspect: "Personalização", civil: "Texto fixo por lei", symbolic: "100% personalizado" },
  { aspect: "Duração", civil: "5-10 minutos", symbolic: "20-40 minutos" },
  { aspect: "Local", civil: "Conservatória ou local autorizado", symbolic: "Qualquer lugar" },
  { aspect: "Votos", civil: "Fórmula standard", symbolic: "Escritos pelo casal ou celebrante" },
  { aspect: "Rituais", civil: "Não permitidos", symbolic: "Ilimitados e criativos" },
  { aspect: "Tom emocional", civil: "Formal e burocrático", symbolic: "Íntimo e emotivo" },
  { aspect: "Participação de convidados", civil: "Apenas testemunhas", symbolic: "Leituras, bênçãos, rituais em grupo" },
  { aspect: "Valor legal", civil: "Sim", symbolic: "Não (complementar ao civil)" }
];

const CelebrantePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/35553754/pexels-photo-35553754.jpeg"
            alt="Cerimónia simbólica"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C2C2C]/60 via-[#2C2C2C]/40 to-[#FAF9F7]" />
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
            <Heart size={16} className="text-white" />
            <span className="text-white text-sm tracking-wider uppercase">Celebrante de Casamentos</span>
          </div>
          
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl text-white mb-6 leading-tight">
            Porquê uma Cerimónia Simbólica?
          </h1>
          
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
            Porque o vosso amor merece mais do que uma formalidade. 
            Merece palavras que vos representem, rituais que vos emocionem, 
            e um momento que nunca vão esquecer.
          </p>
          
          <button
            onClick={() => document.getElementById('contact-cta').scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#6B4E37] rounded-lg font-medium hover:bg-[#FAF9F7] transition-all duration-300 shadow-lg"
            data-testid="hero-cta-btn"
          >
            Quero saber mais
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-6 bg-[#FAF9F7]">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="w-12 h-12 text-[#C4B5A0] mx-auto mb-6" />
          
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-8">
            Uma cerimónia feita à vossa medida
          </h2>
          
          <div className="space-y-6 text-lg text-[#6B4E37] leading-relaxed">
            <p>
              A cerimónia civil cumpre um propósito legal. Mas sejamos honestos: 
              é difícil emocionar-se com um texto que ouviram em dezenas de casamentos antes do vosso.
            </p>
            <p>
              Uma cerimónia simbólica é diferente. É construída a partir da <strong>vossa história</strong>: 
              como se conheceram, o que vos faz rir, os obstáculos que ultrapassaram juntos, 
              os sonhos que partilham.
            </p>
            <p>
              É uma cerimónia para <strong>sentir</strong>, não apenas para cumprir. 
              E os vossos convidados vão notar a diferença.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-6 bg-white/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-caption text-[#C4B5A0] mb-4">Entender a diferença</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
              Civil vs. Simbólica
            </h2>
            <div className="w-16 h-0.5 bg-[#C4B5A0] mx-auto" />
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 bg-[#FAF9F7]">
              <div className="p-4 md:p-6 font-medium text-[#8B7D6B]">Aspeto</div>
              <div className="p-4 md:p-6 font-medium text-[#8B7D6B] text-center border-x border-[#E8E4E0]">
                Cerimónia Civil
              </div>
              <div className="p-4 md:p-6 font-medium text-[#6B4E37] text-center">
                Cerimónia Simbólica
              </div>
            </div>

            {/* Rows */}
            {comparisonData.map((row, index) => (
              <div 
                key={index} 
                className={`grid grid-cols-3 ${index !== comparisonData.length - 1 ? 'border-b border-[#E8E4E0]' : ''}`}
              >
                <div className="p-4 md:p-6 text-[#2C2C2C] font-medium text-sm md:text-base">
                  {row.aspect}
                </div>
                <div className="p-4 md:p-6 text-[#8B7D6B] text-center text-sm md:text-base border-x border-[#E8E4E0] flex items-center justify-center gap-2">
                  {row.aspect === "Valor legal" ? (
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle size={16} className="text-[#C4B5A0] flex-shrink-0 opacity-50" />
                  )}
                  <span>{row.civil}</span>
                </div>
                <div className="p-4 md:p-6 text-[#6B4E37] text-center text-sm md:text-base flex items-center justify-center gap-2">
                  {row.aspect === "Valor legal" ? (
                    <XCircle size={16} className="text-[#C4B5A0] flex-shrink-0 opacity-50" />
                  ) : (
                    <CheckCircle size={16} className="text-[#B5C4B1] flex-shrink-0" />
                  )}
                  <span>{row.symbolic}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-[#8B7D6B] mt-6 text-sm">
            💡 <strong>Dica:</strong> Muitos casais fazem o civil num momento íntimo e reservam a cerimónia simbólica para a celebração principal.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-6 bg-[#FAF9F7]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-caption text-[#C4B5A0] mb-4">Inspiração</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
              Momentos que ficam para sempre
            </h2>
            <div className="w-16 h-0.5 bg-[#C4B5A0] mx-auto" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className="relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={image.url}
                  alt={image.caption}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <p className="absolute bottom-4 left-4 right-4 text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {image.caption}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-white/60">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-caption text-[#C4B5A0] mb-4">Histórias reais</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
              O que dizem os casais
            </h2>
            <div className="w-16 h-0.5 bg-[#C4B5A0] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
                data-testid={`testimonial-${index}`}
              >
                <Quote size={32} className="text-[#C4B5A0]/40 mb-4" />
                
                <p className="text-[#6B4E37] leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>
                
                <div className="border-t border-[#E8E4E0] pt-6">
                  <p className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C] mb-2">
                    {testimonial.couple}
                  </p>
                  <p className="text-sm text-[#8B7D6B] mb-1">
                    <MapPin size={14} className="inline mr-1" />
                    {testimonial.ceremony}
                  </p>
                  <p className="text-sm text-[#C4B5A0]">
                    <Sparkles size={14} className="inline mr-1" />
                    {testimonial.highlight}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-[#FAF9F7]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-caption text-[#C4B5A0] mb-4">Dúvidas frequentes</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
              Perguntas & Respostas
            </h2>
            <div className="w-16 h-0.5 bg-[#C4B5A0] mx-auto" />
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-sm"
                data-testid={`faq-${index}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#FAF9F7] transition-colors"
                >
                  <span className="font-medium text-[#2C2C2C] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp size={20} className="text-[#C4B5A0] flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-[#C4B5A0] flex-shrink-0" />
                  )}
                </button>
                
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-[#6B4E37] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-cta" className="py-20 px-6 bg-gradient-to-br from-[#D4C4B0]/30 to-[#B5C4B1]/30">
        <div className="max-w-4xl mx-auto text-center">
          <Heart size={48} className="text-[#C4B5A0] mx-auto mb-6" />
          
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-6">
            Vamos criar a vossa cerimónia?
          </h2>
          
          <p className="text-lg text-[#6B4E37] mb-8 max-w-2xl mx-auto">
            Cada história de amor é única. Contem-me a vossa e juntos criamos 
            uma cerimónia que vos vai emocionar — e a todos os que vos rodeiam.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-all duration-300 shadow-md"
              data-testid="contact-cta-btn"
            >
              <MessageCircle size={20} />
              Entrar em contacto
            </button>
            
            <button
              onClick={() => navigate('/simulador')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#6B4E37] rounded-lg font-medium hover:bg-[#FAF9F7] transition-all duration-300 shadow-md"
              data-testid="simulator-cta-btn"
            >
              <Sparkles size={20} />
              Fazer o Simulador
            </button>
          </div>

          <p className="text-sm text-[#8B7D6B] mt-8">
            Respondo a todas as mensagens em menos de 24 horas.
          </p>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4">{selectedImage.caption}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CelebrantePage;
